import {createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuOptionWithFloatingSubmenu from 'ui-library/menus/MenuOptionWithFloatingSubmenu';
import {defaultRenderOptionContent, getOptionRefForIndex} from 'ui-library/menus/MenuUtil';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';

import {
  LIBRARY_PARAMETER_DEFINITION_DATA_TYPES,
  LibraryParameterDefinitionDataType,
} from '../ParameterLibraryTypes';
import {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME} from './parameterLibraryListingUtils';

export interface SchemaFieldSelectorOptionValue {
  id: string;
  name: string;
}

const useStyles = createStyles(() => ({
  icon: {
    marginRight: '4px',
  },
  selectedLabel: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const DATA_TYPE_TO_ICON: Record<LibraryParameterDefinitionDataType, BenchlingIconName> = {
  [FieldDefinitionType.FLOAT]: 'number',
  [FieldDefinitionType.TEXT]: 'text',
  [FieldDefinitionType.DATETIME]: 'calendar',
  [FieldDefinitionType.DROPDOWN]: 'circle-caret-down',
};

/** @package */
export interface LibraryParameterDefinitionDataTypeSelectorProps {
  selectedType: LibraryParameterDefinitionDataType;
  onChangeSelection: (parameterType: LibraryParameterDefinitionDataType) => void;
  onSelectDropdown?: (selector: SchemaFieldSelectorOptionValue | null) => void;
  selectedDropdownName?: string | null;
  dropdownOptions?: Array<SchemaFieldSelectorOptionValue>;
}

export function LibraryParameterDefinitionDataTypeSelector(
  props: LibraryParameterDefinitionDataTypeSelectorProps
): JSX.Element {
  const {classes} = useStyles();
  const [optionRefs, setOptionRefs] = useState<{[ref: string]: HTMLLIElement | null}>({});
  const selectorOptions = props.dropdownOptions ?? [];

  const placeholderText = 'Select data type';
  const selectedOptionLabel = props.selectedType ? (
    <div className={classes.selectedLabel}>
      <BenchlingIcon icon={DATA_TYPE_TO_ICON[props.selectedType]} className={classes.icon} />
      <span>
        {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[props.selectedType]}
        {props.selectedType === FieldDefinitionType.DROPDOWN && props.selectedDropdownName
          ? `: ${props.selectedDropdownName}`
          : ''}
      </span>
    </div>
  ) : (
    placeholderText
  );

  const dropdownOptionIndex = LIBRARY_PARAMETER_DEFINITION_DATA_TYPES.indexOf(FieldDefinitionType.DROPDOWN);

  return (
    <SelectDropdown
      displayName={selectedOptionLabel}
      buttonSize={ButtonSize.MINI}
      menu={
        <Menu value={props.selectedType}>
          {LIBRARY_PARAMETER_DEFINITION_DATA_TYPES.map(
            (type: LibraryParameterDefinitionDataType, index: number) => {
              if (type === FieldDefinitionType.DROPDOWN && props.onSelectDropdown) {
                return (
                  <OptionContainer
                    key={type}
                    ref={(node) => {
                      if (node != null) {
                        const optionRefName = getOptionRefForIndex(dropdownOptionIndex);
                        if (optionRefs[optionRefName] == null) {
                          setOptionRefs((prevRefs) => ({
                            ...prevRefs,
                            [optionRefName]: node.getRef(),
                          }));
                        }
                      }
                    }}
                  >
                    <MenuOptionWithFloatingSubmenu
                      menuOptionContainer={optionRefs[getOptionRefForIndex(dropdownOptionIndex)]}
                      menuOptionIndex={index}
                      optionData={{
                        name: LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[type],
                        icon: DATA_TYPE_TO_ICON[type],
                        disabled: false,
                        submenu: [[]],
                        dataTestAttrs: {
                          component: 'LibraryParameterDefinitionDataTypeSelector',
                          element: 'dropdownOption',
                        },
                      }}
                      submenuProps={{
                        width: MenuWidth.LARGE,
                        scrollable: true,
                      }}
                      renderOptionContentFn={defaultRenderOptionContent}
                    >
                      {selectorOptions.length === 0
                        ? [
                            <Option key="empty" isDisabled={true}>
                              No dropdowns found
                            </Option>,
                          ]
                        : selectorOptions.map((selector) => (
                            <Option
                              key={selector.id}
                              onSelect={() => {
                                props.onChangeSelection(FieldDefinitionType.DROPDOWN);
                                props.onSelectDropdown?.(selector);
                              }}
                            >
                              {selector.name}
                            </Option>
                          ))}
                    </MenuOptionWithFloatingSubmenu>
                  </OptionContainer>
                );
              }
              return (
                <Option
                  icon={DATA_TYPE_TO_ICON[type]}
                  key={type}
                  value={type}
                  onSelect={() => {
                    props.onChangeSelection(type);
                  }}
                  isSelected={props.selectedType === type}
                >
                  {LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[type]}
                </Option>
              );
            }
          )}
        </Menu>
      }
      dataTestAttrs={{component: 'LibraryParameterDefinitionDataTypeSelector', element: 'dropdown'}}
    />
  );
}
